import { create } from "zustand";

/** R9-A13: a toast may carry one action (Undo) and then stays 8 s. `onExpire` runs when it
 *  goes by itself, never after its action: a deferred delete commits there. */
export type ToastAction = { label: string; run: () => void };
export type ToastOptions = {
  error?: boolean;
  action?: ToastAction;
  ms?: number;
  onExpire?: () => void;
};
type Toast = { id: number; text: string; error?: boolean; action?: ToastAction };

export const TOAST_MS = 4000;
export const ACTION_TOAST_MS = 8000;

/** Phones (R7.5 contract 4) show the list column or the content, one at a time: what was asked
 *  for, by address (a tab shows its list, a row its item, Back the list again). Anywhere else
 *  the address decides (components/shell/place.ts). By address, so asking for the next one
 *  never changes what the current one shows while the router moves (a page shown for a moment
 *  would mark its conversation read). */
export type PhoneViews = Record<string, "list" | "main">;

type UiState = {
  paletteOpen: boolean;
  toasts: Toast[];
  // Text to pre-fill (unsent) in a conversation's composer when it opens, e.g. "Set up a
  // new teammate: " from People (R3 contract 12).
  draft: { conversationId: string; text: string } | null;
  phone: PhoneViews;
  setPalette: (open: boolean) => void;
  /** Show a toast; returns its id. `error` may be the options object. */
  toast: (text: string, error?: boolean | ToastOptions) => number;
  /** Its action was pressed: run it, close the toast, and don't expire it. */
  actOnToast: (id: number) => void;
  dismissToast: (id: number) => void;
  /** Take it away without its action or its expiry (what it offered has already happened). */
  dropToast: (id: number) => void;
  setDraft: (draft: { conversationId: string; text: string } | null) => void;
  /** What a phone shows at `href`. */
  setPhone: (href: string, view: "list" | "main") => void;
  /** Once at `href`: forget what was asked for anywhere else. */
  keepPhone: (href: string) => void;
};

let nextToast = 1;
const expiries = new Map<number, () => void>();

export const useUi = create<UiState>((set, get) => ({
  paletteOpen: false,
  toasts: [],
  draft: null,
  phone: {},
  setDraft: (draft) => set({ draft }),
  setPalette: (open) => set({ paletteOpen: open }),
  setPhone: (href, view) => set((s) => ({ phone: { ...s.phone, [href]: view } })),
  keepPhone: (href) =>
    set((s) => {
      const keys = Object.keys(s.phone);
      if (keys.every((k) => k === href)) return s;
      const view = s.phone[href];
      return { phone: view ? { [href]: view } : {} };
    }),
  toast: (text, error) => {
    const opts: ToastOptions = typeof error === "object" ? error : { error };
    // One Undo at a time (review R10: an archive's toast and a delete's stacked two Undo
    // buttons): a new action toast takes over, and the one it replaces goes as if it had
    // timed out (a deferred delete commits).
    if (opts.action)
      for (const old of get().toasts.filter((t) => t.action)) get().dismissToast(old.id);
    const id = nextToast++;
    if (opts.onExpire) expiries.set(id, opts.onExpire);
    set((s) => ({ toasts: [...s.toasts, { id, text, error: opts.error, action: opts.action }] }));
    const ms = opts.ms ?? (opts.action ? ACTION_TOAST_MS : TOAST_MS);
    setTimeout(() => {
      if (!get().toasts.some((t) => t.id === id)) return; // acted on or dismissed
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
      const expire = expiries.get(id);
      expiries.delete(id);
      expire?.();
    }, ms);
    return id;
  },
  actOnToast: (id) => {
    const t = get().toasts.find((x) => x.id === id);
    expiries.delete(id);
    set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
    t?.action?.run();
  },
  dismissToast: (id) => {
    const expire = expiries.get(id);
    expiries.delete(id);
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    expire?.();
  },
  dropToast: (id) => {
    expiries.delete(id);
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
}));
